import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DeviceType, Group } from '@wix/presence-client';
import { Duplexer } from '@wix/duplexer-js';
import { useEnvironment, usePanorama } from '@wix/yoshi-flow-editor';

import { useRootProps } from 'AiAssistantWidget/Widget/contexts';

import { useAppDispatch, useAppSelector } from '../store';
import { getGeoLocation, selectGeoLocation, selectMessages } from '../features';
import { toPanoramaError } from '../utils';

const KEEP_ALIVE_INTERVAL = 90 * 1000; // 1.5 minutes
const IDLE_TIMEOUT = 3 * 60 * 60 * 1000; // 3 hours
const IMPORT_RETRY_DELAY = 1000;

// The chunk request is aborted when a visitor leaves right after load
// (mostly iOS Safari), so give it one more chance before giving up.
const importPresenceClient = async () => {
  try {
    return await import('@wix/presence-client');
  } catch {
    await new Promise((resolve) => setTimeout(resolve, IMPORT_RETRY_DELAY));
    return import('@wix/presence-client');
  }
};

export interface Presence {
  deviceType: DeviceType;
  location: string;
  timestamp: string | number;
  pageName: string;
}

export const usePresenceClient = () => {
  const dispatch = useAppDispatch();
  const panorama = usePanorama();

  const { isEditor, isMobile } = useEnvironment();
  const [, setPresence] = useState<Presence>({} as Presence);
  const conversationId = useAppSelector(selectMessages.conversationId);
  const shouldLoadLocation = useAppSelector(selectGeoLocation.shouldLoad);
  const geo = useAppSelector(selectGeoLocation.value);

  const { instanceToken, page } = useRootProps();

  const deviceType = useMemo(
    () => (isMobile ? DeviceType.MOBILE : DeviceType.DESKTOP),
    [isMobile],
  );

  const presenceGroup = useRef<Group<Presence>>();
  const initializedTokenRef = useRef<string>();
  // The import below can take a while (it is retried once), so the initial
  // state must come from the latest render, not the one that started it.
  const latestRef = useRef({
    deviceType,
    location: geo?.location ?? '',
    pageName: page?.name ?? '',
  });
  useEffect(() => {
    latestRef.current = {
      deviceType,
      location: geo?.location ?? '',
      pageName: page?.name ?? '',
    };
  }, [deviceType, geo?.location, page?.name]);
  const getSignedInstance = useMemo(
    () => () => instanceToken!,
    [instanceToken],
  );

  const connectPresence = useCallback(
    () =>
      presenceGroup.current?.track(() => {}, {
        watchGroup: false,
      }),
    [],
  );

  const updatePresence = useCallback((newPresence: Partial<Presence>) => {
    setPresence((currentPresence) => {
      const updatedPresence = {
        ...currentPresence,
        ...newPresence,
      };

      if (updatedPresence.pageName && presenceGroup.current) {
        void presenceGroup.current.setState(updatedPresence);
      }

      return updatedPresence;
    });
  }, []);

  useEffect(() => {
    if (initializedTokenRef.current === instanceToken) {
      return;
    }
    initializedTokenRef.current = instanceToken;

    void (async () => {
      if (isEditor) {
        return;
      }

      let presenceClient: Awaited<ReturnType<typeof importPresenceClient>>;
      try {
        presenceClient = await importPresenceClient();
      } catch (error) {
        // Presence is non-essential: no further attempts for this token (also
        // keeps this effect from re-importing on every dependency change)
        const { error: err, tags } = toPanoramaError(error, {
          feature: 'presence',
          action: 'importPresenceClient',
        });
        panorama.errorMonitor().reportError(err, { tags });
        return;
      }
      if (initializedTokenRef.current !== instanceToken) {
        // The token changed while importing: the newer run owns initialization
        return;
      }
      const { default: PresenceClient, Scope } = presenceClient;

      const presenceInstance = new PresenceClient({
        duplexer: new Duplexer('duplexer.wix.com', {
          instanceUpdater: {
            getInstance: getSignedInstance,
          },
        }),
        getSignedInstance,
      });

      const initialState = {
        ...latestRef.current,
        timestamp: Date.now(),
      };
      setPresence(initialState);

      presenceGroup.current = presenceInstance.group(Scope.VIEWER, {
        initialState,
        keepAliveInterval: KEEP_ALIVE_INTERVAL,
        onIdle: () => {
          presenceGroup.current?.disconnect();
        },
        onActive: connectPresence,
        isIdleMs: IDLE_TIMEOUT,
      });
      connectPresence();
    })();
  }, [connectPresence, getSignedInstance, instanceToken, isEditor, panorama]);

  useEffect(() => {
    if (shouldLoadLocation) {
      dispatch(getGeoLocation());
    }
  }, [shouldLoadLocation, dispatch]);

  useEffect(() => {
    updatePresence({
      pageName: page?.name ?? '',
    });
  }, [page?.name, updatePresence]);

  useEffect(() => {
    updatePresence({
      location: geo?.location ?? '',
    });
  }, [geo?.location, updatePresence]);

  useEffect(() => {
    updatePresence({});
  }, [conversationId, updatePresence]); // update presence on conversationId change
};
